import { ParsedFilingFieldErrorState } from "components/chat/FilingDraftReview/ParsedFilingFieldErrorState";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { EditPencil } from "components/icons/EditPencil";
import { SmallInfo } from "components/icons/SmallInfo";
import { TrashSimple } from "components/icons/TrashSimple";
import { Form, Formik } from "formik";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import {
  Parsed1120FieldType,
  ParsedShareholder,
} from "store/apis/filingDraftReview";
import { capitalize } from "utils/capitalize";
import { DeleteField } from "./DeleteField";
import { Tag } from "components/DesignSystem/Tag/Tag";

export type Parsed1120FieldProps = {
  field: Parsed1120FieldType;
  isEdited?: boolean;
  onDelete?: () => void;
  onEdit?: (newValue: string | number) => void;
  onArrayEdit?: (values: ParsedShareholder[]) => void;
  isEditable: boolean;
  isEditing?: boolean;
  setEditFieldKey?: (key: string) => void;
  disableActions?: boolean;
  removeWarning?: () => void;
  isRemoved?: boolean;
};

const TooltipComponent = ({
  tooltip,
  isEdited,
}: {
  tooltip: string | undefined;
  isEdited: boolean | undefined;
}) => {
  if (!tooltip) return null;
  return (
    <ToolTip
      text={
        <div
          className="t-text-left"
          dangerouslySetInnerHTML={{
            __html: isEdited
              ? "This value was manually edited by the preparer, so a system-generated explanation isn't available."
              : tooltip,
          }}
        ></div>
      }
    >
      <span className="t-text-text-30 t-ml-1 -t-mt-0.5">
        <SmallInfo />
      </span>
    </ToolTip>
  );
};

const ShareholderInfoComponent = ({
  value,
  fieldKey,
  isAdmin,
  isEdited,
}: {
  value: Parsed1120FieldType["value"];
  fieldKey: string;
  isAdmin: boolean;
  isEdited: boolean | undefined;
}) => {
  if (!Array.isArray(value) || fieldKey !== "shareholder_info") return null;
  return (
    <div>
      {(value as { name: string; percentage: string }[]).map((shareholder) => (
        <div key={shareholder.name} className="t-text-text-100 t-text-body-sm">
          {capitalize(shareholder.name)} (
          {(shareholder.percentage || "").toString()})
        </div>
      ))}
      {isAdmin && isEdited && (
        <Tag tagType="gray" size="small" rounded icon={false}>
          Edited
        </Tag>
      )}
    </div>
  );
};

const EditFormComponent = ({
  isEditing,
  value,
  fieldKey,
  setEditFieldKey,
  onEdit,
  onArrayEdit,
}: {
  isEditing: boolean | undefined;
  value: Parsed1120FieldType["value"];
  fieldKey: string;
  setEditFieldKey: ((key: string) => void) | undefined;
  onEdit: ((newValue: string | number) => void) | undefined;
  onArrayEdit: ((values: ParsedShareholder[]) => void) | undefined;
}) => {
  if (!isEditing) return null;

  const handleSubmit = (values: { [key: string]: string | number }) => {
    onEdit?.(values[fieldKey]);
  };

  const handleArrayUpdate = (values: ParsedShareholder[]) => {
    onArrayEdit?.(values as ParsedShareholder[]);
  };

  if (Array.isArray(value)) {
    const initialValues = value.map((fieldData) => {
      const obj: { [K in keyof ParsedShareholder]: string } = {
        name: "",
        percentage: "",
      };
      (Object.keys(fieldData) as (keyof ParsedShareholder)[]).forEach((cv) => {
        obj[cv] = fieldData[cv] ? String(fieldData[cv]) : "";
      });
      return obj;
    });

    const fieldLabels: Record<string, string> = {
      name: "Name",
      percentage: "Percentage",
    };

    return (
      <Formik
        validateOnChange
        validationSchema={null}
        onSubmit={(values) =>
          handleArrayUpdate(
            (values.array as { [key: string]: string }[]).map((item) => ({
              name: item.name,
              percentage: item.percentage,
            }))
          )
        }
        initialValues={{ array: initialValues }}
      >
        {({ values, handleChange }) => (
          <Form className="t-m-0 t-space-y-4 t-mt-3">
            <div className="t-flex t-gap-2 t-flex-col t-items-end">
              {values.array.map((fieldData: any, idx: number) => (
                <div key={idx} className="t-space-y-1">
                  <div className="t-text-subtext-sm t-text-text-60">
                    Shareholder {idx + 1}
                  </div>
                  <div className="t-flex t-gap-2">
                    {(
                      Object.keys(fieldData) as (keyof ParsedShareholder)[]
                    ).map((fieldName) => (
                      <TextInput
                        label=""
                        block
                        placeholder={`Enter ${
                          fieldLabels[fieldName] || fieldName
                        }`}
                        name={`array[${idx}].${fieldName}`}
                        customSize="small"
                        key={fieldName}
                        value={values.array[idx][fieldName]}
                        onChange={handleChange}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="t-flex t-gap-1.5">
              <Button customType="primary" size="small" type="submit">
                Save
              </Button>
              <Button
                type="button"
                size="small"
                onClick={() => setEditFieldKey?.("")}
              >
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <div>
      <Formik
        validateOnChange
        validationSchema={null}
        onSubmit={handleSubmit}
        initialValues={{
          [fieldKey]: (value || "") as string | number,
        }}
      >
        <Form className="t-m-0 t-flex t-gap-2 t-items-end">
          <TextInput
            label=""
            block
            placeholder="Enter value"
            name={fieldKey}
            customSize="small"
          />
          <Button customType="primary" size="small">
            Save
          </Button>
          <Button
            type="button"
            size="small"
            onClick={() => setEditFieldKey?.("")}
          >
            Cancel
          </Button>
        </Form>
      </Formik>
    </div>
  );
};

const ValueComponent = ({
  isEditing,
  value,
  fieldKey,
  isAdmin,
  isEdited,
  setEditFieldKey,
  onEdit,
  onArrayEdit,
}: {
  isEditing: boolean | undefined;
  value: Parsed1120FieldType["value"];
  fieldKey: string;
  isAdmin: boolean;
  isEdited: boolean | undefined;
  setEditFieldKey: ((key: string) => void) | undefined;
  onEdit: ((newValue: string | number) => void) | undefined;
  onArrayEdit: ((values: ParsedShareholder[]) => void) | undefined;
}) => {
  if (isEditing) {
    return (
      <EditFormComponent
        isEditing={isEditing}
        value={value}
        fieldKey={fieldKey}
        setEditFieldKey={setEditFieldKey}
        onEdit={onEdit}
        onArrayEdit={onArrayEdit}
      />
    );
  }

  if (Array.isArray(value)) {
    return (
      <ShareholderInfoComponent
        value={value}
        fieldKey={fieldKey}
        isAdmin={isAdmin}
        isEdited={isEdited}
      />
    );
  }

  return (
    <div className="t-text-text-100 t-text-body-sm">
      {value === null ? "-" : value}
      {isAdmin && isEdited && (
        <span className="t-ml-1.5">
          <Tag tagType="gray" size="small" rounded icon={false}>
            Edited
          </Tag>
        </span>
      )}
    </div>
  );
};

const ActionsComponent = ({
  isEditable,
  isEditing,
  setEditFieldKey,
  fieldKey,
  disableActions,
  onOpenDeleteModal,
}: {
  isEditable: boolean;
  isEditing: boolean | undefined;
  setEditFieldKey: ((key: string) => void) | undefined;
  fieldKey: string;
  disableActions: boolean | undefined;
  onOpenDeleteModal: () => void;
}) => {
  if (!isEditable || isEditing) return null;

  return (
    <div className="t-flex t-items-center t-gap-2">
      <Button
        size="small"
        customType="icon"
        onClick={() => setEditFieldKey?.(fieldKey)}
        disabled={disableActions}
      >
        <EditPencil className="t-text-text-30" />
      </Button>
      <Button
        size="small"
        customType="icon"
        onClick={onOpenDeleteModal}
        disabled={disableActions}
      >
        <span className="t-text-text-30">
          <TrashSimple />
        </span>
      </Button>
    </div>
  );
};

export const Parsed1120Field = ({
  field,
  isEdited,
  onDelete,
  onEdit,
  onArrayEdit,
  isEditable,
  isEditing,
  setEditFieldKey,
  disableActions,
  removeWarning,
  isRemoved,
}: Parsed1120FieldProps) => {
  const deleteModal = useModal();
  const { isAdmin } = useRoleBasedView();
  const {
    key: fieldKey,
    label,
    value,
    tooltip,
    error_message: errorMessage,
    warning_message: warningMessage,
  } = field || {};
  const isRemovable = Boolean(value) || value === 0;

  const handleSubmit = (values: { [key: string]: string | number }) => {
    onEdit?.(values[fieldKey]);
  };

  const handleArrayUpdate = (values: ParsedShareholder[]) => {
    onArrayEdit?.(values as ParsedShareholder[]);
  };

  return (
    <div className="t-w-full t-border t-border-solid t-border-l-0 t-border-t-0 t-border-r-0 t-border-neutral-10 t-px-8 t-py-5 t-space-y-1.5">
      <div className="t-flex t-items-center t-gap-8 t-w-full t-justify-between">
        <div>
          <div className="t-text-text-30 t-text-body-sm t-flex t-items-center">
            {label}
            <TooltipComponent tooltip={tooltip} isEdited={isEdited} />
          </div>
          <ValueComponent
            isEditing={isEditing}
            value={value}
            fieldKey={fieldKey}
            isAdmin={isAdmin}
            isEdited={isEdited}
            setEditFieldKey={setEditFieldKey}
            onEdit={onEdit}
            onArrayEdit={onArrayEdit}
          />
        </div>
        <ActionsComponent
          isEditable={isEditable}
          isEditing={isEditing}
          setEditFieldKey={setEditFieldKey}
          fieldKey={fieldKey}
          disableActions={disableActions}
          onOpenDeleteModal={deleteModal.open}
        />
      </div>
      {isEditable &&
        (errorMessage || warningMessage) &&
        !isEditing &&
        !isEdited &&
        !isRemoved && (
          <ParsedFilingFieldErrorState
            errorMessage={errorMessage}
            warningMessage={warningMessage}
            isRemovable={isRemovable}
            removeWarning={removeWarning}
          />
        )}
      <DeleteField
        {...deleteModal}
        onDelete={() => onDelete?.()}
        fieldLabel={label}
      />
    </div>
  );
};
